import { useEffect, useRef, useCallback, useState } from 'react';

interface IdleDetectionOptions {
  idleTimeout?: number; // Time in ms before considered idle (default: 2 minutes)
  onIdle?: () => void;
  onActive?: () => void;
  enabled?: boolean;
}

/**
 * Hook to detect user idle state and pause expensive operations
 * Does NOT logout user, just pauses resource-intensive operations
 */
export const useIdleDetection = (options?: IdleDetectionOptions) => {
  const {
    idleTimeout = 2 * 60 * 1000, // 2 minutes
    onIdle,
    onActive,
    enabled = true,
  } = options ?? {};

  const [isIdle, setIsIdle] = useState(false);
  // Use ReturnType<typeof setTimeout> so the hook works in both browser and Node environments
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActiveRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    if (enabled) {
      lastActiveRef.current = Date.now();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (isIdle) {
        setIsIdle(false);
        onActive?.();
      }

      const t = window.setTimeout(() => {
        setIsIdle(true);
        onIdle?.();
      }, idleTimeout);
      timeoutRef.current = t as unknown as ReturnType<typeof setTimeout>;
    }
  }, [enabled, idleTimeout, isIdle, onIdle, onActive]);

  useEffect(() => {
    if (!enabled) {
      setIsIdle(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Provide an explicit cleanup function so return type is consistent
      return () => {};
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetTimer();
    };

    // Initialize
    resetTimer();

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, resetTimer]);

  return {
    isIdle,
    lastActive: lastActiveRef.current,
  };
};
